import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { getValueGetter } from 'module/ui/components/atoms/grid/getters/get-value-getter';
import { getValueFormatter } from 'module/ui/components/atoms/grid/value-formatter';
import { Short00, ShortSB } from 'module/ui/components/styles';
import {
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { AccountPeopleProfile } from 'api/account-people/profile-type';
import { useExternalTeamMemberFieldsAsFilterableField } from 'helpers/fields/external-team-members-fields/fields';
import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Collapsible } from './collapsible-section';
import { getRenderer } from '../../list/grid/cell-renderer';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
`;
const FieldLabel = styled.div`
  ${Short00};
  color: var(--text-secondary);
`;
const FieldValue = styled.div`
  ${ShortSB};
`;

interface Props {
  userProfile: AccountPeopleProfile;
}
const FieldRenderer = ({
  field,
  userRecord,
}: {
  field: FilterableField;
  userRecord: Record<string, any>;
}) => {
  const value = useMemo(() => {
    const renderer = getRenderer(field);
    const formatter = getValueFormatter(field);
    const valueGetter = getValueGetter(field);
    const getValue = () =>
      valueGetter != null
        ? valueGetter({ data: userRecord } as unknown as ValueGetterParams)
        : userRecord?.[field.fieldName];
    const value =
      renderer != null && typeof renderer === 'function'
        ? renderer({
            value: getValue(),
            data: userRecord,
          } as unknown as ICellRendererParams)
        : getValue();

    return formatter != null
      ? formatter({
          value,
          data: userRecord,
        } as unknown as ValueFormatterParams)
      : value;
  }, [field, userRecord]);

  return (
    <Field key={field.fieldId}>
      <FieldLabel>{field.fieldLabel}</FieldLabel>
      <FieldValue>{value}</FieldValue>
    </Field>
  );
};

export const MoreFields: React.FC<Props> = ({ userProfile: { user } }) => {
  const { fields: externalTeamMemberFields } =
    useExternalTeamMemberFieldsAsFilterableField();
  const fieldLookup = useMemo(
    () => keyBy(externalTeamMemberFields, 'fieldId'),
    [externalTeamMemberFields],
  );
  const transformedUser = useMemo(() => {
    return {
      ...user,
      ...user.fields?.reduce(
        (acc, field) => {
          const targetField = fieldLookup[field.fieldId];

          if (targetField) {
            acc[targetField.fieldName] = field.fieldValue;
          }

          return acc;
        },
        {} as Record<string, any>,
      ),
    };
  }, [user, fieldLookup]);

  return (
    <Collapsible
      title={
        <FormattedMessage
          id="account.people.profile.sections.more_fields.title"
          values={{ name: user.firstName ?? user.lastName }}
        />
      }
      panelKey="more-fields"
    >
      <Wrapper>
        {externalTeamMemberFields.map((field) => (
          <FieldRenderer
            key={field.fieldId}
            field={field}
            userRecord={transformedUser}
          />
        ))}
      </Wrapper>
    </Collapsible>
  );
};
